import { useAuth } from "@clerk/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  AppointmentListItem,
  AppointmentListResponse,
} from "../../../shared/api-types.js";
import { pagesMirrorPoll } from "./mirror-state";
import { authenticatedApi } from "./api.js";
import {
  appointmentBatchCount,
  appointmentsListApiRequest,
  appointmentsTodayCount,
  deviceTimezone,
  formatAppointmentTime,
  groupAppointments,
  keepsPreviousAppointmentRows,
  scheduleRowLabel,
  splitEarlierAppointments,
  upNextAppointment,
  type AppointmentDateGroup,
  type AppointmentListSearch,
  type UpNextAppointment,
} from "./appointments.js";

type AvailableAppointmentList = Extract<
  AppointmentListResponse,
  { status: "available" }
>;
type UnavailableAppointmentList = Extract<
  AppointmentListResponse,
  { status: "unavailable" }
>;

export interface AppointmentHomeSection {
  key: string;
  label: string;
  rows: Array<{ item: AppointmentListItem; timeLabel: string }>;
}

interface AppointmentAgendaReady {
  status: "ready";
  display: { now: string; timezone: string };
  healthieCalendarUrl: string;
  sections: AppointmentHomeSection[];
  upNext: UpNextAppointment | null;
  endedTodayIds: ReadonlySet<string>;
  todayCount: number | null;
  itemCount: number;
  loadedBatches: number;
  hasMore: boolean;
  isFetching: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
}

export type AppointmentAgenda =
  | AppointmentAgendaReady
  | { status: "loading" }
  | { status: "unavailable"; reason: UnavailableAppointmentList["reason"] }
  /** Admin, no RD named. Distinct from unavailable: nothing is broken. */
  | { status: "no_selection" }
  | { status: "error"; error: Error; retry: () => void };

/**
 * `enabled` is false on views that never show the Upcoming list (Availability,
 * NO-878): no Appointment list read runs, and the answer stays loading.
 */
export function useAppointmentAgenda(
  search: AppointmentListSearch,
  enabled = true
): AppointmentAgenda {
  const appointments = useAppointmentListQuery(search, enabled);
  useBatchReplay(appointments, appointmentBatchCount(search));

  if (appointments.isError) {
    return {
      status: "error",
      error: appointments.error,
      retry: () => {
        void appointments.refetch();
      },
    };
  }

  const pages = appointments.data?.pages;
  if (!pages || pages.length === 0) return { status: "loading" };

  if (pages.some((page) => page.data.status === "no_selection")) {
    return { status: "no_selection" };
  }

  const unavailable = pages
    .map((page) => page.data)
    .find((data): data is UnavailableAppointmentList => data.status === "unavailable");
  if (unavailable) return { status: "unavailable", reason: unavailable.reason };

  return readyAgenda(
    pages
      .map((page) => page.data)
      .filter((data): data is AvailableAppointmentList => data.status === "available"),
    appointments
  );
}

function useAppointmentListQuery(search: AppointmentListSearch, enabled: boolean) {
  const { getToken } = useAuth();
  const showNonDefaultStatuses = search.showNonDefaultStatuses === true;
  const requestedTimezone = deviceTimezone();
  const rdId = search.rdId;

  return useInfiniteQuery({
    // In the key so switching RDs unmounts the previous RD's rows rather than
    // restyling them.
    queryKey: ["appointments", showNonDefaultStatuses, requestedTimezone, rdId ?? null],
    initialPageParam: null as string | null,
    enabled,
    queryFn: ({ pageParam }) => {
      const request = appointmentsListApiRequest({
        showNonDefaultStatuses,
        cursor: pageParam,
        timezone: requestedTimezone,
        ...(rdId ? { rdId } : {}),
      });

      return authenticatedApi<{ data: AppointmentListResponse }>(
        getToken,
        request.path,
        request.init
      );
    },
    getNextPageParam: (lastPage) =>
      lastPage.data.status === "available"
        ? (lastPage.data.pageInfo.nextCursor ?? undefined)
        : undefined,
    // Rows stay on screen across a filter toggle within one selection only. A
    // change of RD — including Browser Back to no selection — changes the key
    // and must unmount the previous RD's patients, not hold them as placeholder
    // until the next answer arrives.
    placeholderData: (previousData, previousQuery) =>
      keepsPreviousAppointmentRows(previousQuery?.queryKey, rdId)
        ? previousData
        : undefined,
    staleTime: 0,
    gcTime: 0,
    // NO-917: a visit still waiting for its Healthie copy reads again.
    refetchInterval: (query) => pagesMirrorPoll(query.state.data?.pages),
  });
}

type AppointmentListQuery = ReturnType<typeof useAppointmentListQuery>;

/** Replays the batch depth the URL records, one batch at a time. */
function useBatchReplay(
  appointments: AppointmentListQuery,
  targetBatches: number
): void {
  const loadedBatches = appointments.data?.pages.length ?? 0;
  const { fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPlaceholderData } =
    appointments;
  const isSettledAnswer = !isError && !isPlaceholderData;
  const needsAnotherBatch = loadedBatches > 0 && loadedBatches < targetBatches;
  const canFetchNext = hasNextPage && !isFetchingNextPage;
  const replays = isSettledAnswer && needsAnotherBatch && canFetchNext;

  useEffect(() => {
    if (replays) void fetchNextPage();
  }, [
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPlaceholderData,
    loadedBatches,
    replays,
    targetBatches,
  ]);
}

function readyAgenda(
  availablePages: AvailableAppointmentList[],
  appointments: AppointmentListQuery
): AppointmentAgendaReady {
  const firstPage = availablePages[0]!;
  const display = { now: firstPage.now, timezone: firstPage.timezone };
  const itemsById = new Map<string, AppointmentListItem>();

  for (const data of availablePages) {
    for (const item of data.appointments) itemsById.set(item.id, item);
  }

  // The server's ended-this-week lane feeds only today's ended visits, which stay
  // under Today; earlier days of the week now live in the Past view (NO-767).
  const { endedToday } = splitEarlierAppointments(
    firstPage.earlierAppointments,
    display
  );
  const endedTodayIds = new Set(endedToday.map((item) => item.id));

  for (const item of endedToday) {
    if (!itemsById.has(item.id)) itemsById.set(item.id, item);
  }

  const items = [...itemsById.values()];
  const groups = groupAppointments(items, display);
  const hasMore = appointments.hasNextPage === true;

  return {
    status: "ready",
    display,
    healthieCalendarUrl: firstPage.healthieCalendarUrl,
    sections: homeSections(groups, display),
    upNext: upNextAppointment(groups, display),
    endedTodayIds,
    todayCount: appointmentsTodayCount(groups, { ...display, hasNextPage: hasMore }),
    itemCount: items.length,
    loadedBatches: availablePages.length,
    hasMore,
    isFetching: appointments.isFetching,
    isFetchingMore: appointments.isFetchingNextPage,
    loadMore: () => {
      void appointments.fetchNextPage();
    },
  };
}

function homeSections(
  groups: AppointmentDateGroup[],
  display: { now: string; timezone: string }
): AppointmentHomeSection[] {
  const sections: AppointmentHomeSection[] = [];
  const comingUp: AppointmentHomeSection = {
    key: "coming-up",
    label: "Coming up",
    rows: [],
  };

  for (const group of groups) {
    if (group.label === "Today" || group.label === "Tomorrow") {
      sections.push({
        key: group.date,
        label: group.label,
        rows: group.appointments.map((item) => ({
          item,
          timeLabel: formatAppointmentTime(item.startsAt, display.timezone),
        })),
      });
      continue;
    }

    comingUp.rows.push(
      ...group.appointments.map((item) => ({
        item,
        timeLabel: scheduleRowLabel(item.startsAt, display),
      }))
    );
  }

  if (comingUp.rows.length > 0) sections.push(comingUp);

  return sections;
}
